import React from "react";
import { Modal } from "react-bootstrap";
import styled, { keyframes } from "styled-components";
import { formatTHB } from "../../views/SubscriptionProjectV2/lib/formatters";

/**
 * Success modal — shown after the atomic submit succeeds.
 *
 * Communicates 4 things:
 *  1. Payment confirmed — amount that was charged
 *  2. Verify-email reminder — they need to click the link in their inbox
 *  3. Where the tax invoice was sent
 *  4. CTA to product login page
 *
 * Two addresses, two purposes — showing only one sends people to the
 * wrong inbox:
 *   projectEmail  ได้ลิงก์ยืนยัน + ใช้ login เข้าระบบที่สมัคร
 *   billingEmail  ได้ใบเสร็จ/ใบกำกับภาษี + ใช้ login invoize.me
 * มักเป็นคนละอีเมล เช่น ฝ่ายบัญชีรับใบเสร็จ แต่ HR เป็นคนใช้ระบบ
 *
 * @param {string} projectEmail  ปลายทางลิงก์ยืนยัน (account.project_email)
 * @param {string} billingEmail  ปลายทางใบกำกับภาษี (account.email)
 */
function SuccessModal({
  show,
  amount,
  projectEmail,
  billingEmail,
  redirectUrl,
  lang = "th",
}) {
  const handleGo = () => {
    if (redirectUrl) window.location.href = redirectUrl;
  };

  return (
    <StyledModal show={show} backdrop="static" keyboard={false} centered>
      <Body>
        <CheckCircle>
          <CheckMark aria-hidden>✓</CheckMark>
        </CheckCircle>

        <Heading>{lang === "en" ? "Welcome aboard!" : "สมัครสำเร็จ!"}</Heading>

        <Subtitle>
          {lang === "en" ? (
            <>
              We've charged <Bold>{formatTHB(amount)}</Bold> to your card.
            </>
          ) : (
            <>
              ตัดเงินจำนวน <Bold>{formatTHB(amount)}</Bold> เรียบร้อยแล้ว
            </>
          )}
        </Subtitle>

        <Reminder>
          <ReminderTitle>
            <ReminderIcon aria-hidden>📧</ReminderIcon>
            {lang === "en"
              ? "One more step: verify your email"
              : "อีกขั้นเดียว: ยืนยันอีเมล"}
          </ReminderTitle>
          <ReminderText>
            {lang === "en" ? (
              <>
                We sent a verification link to <Bold>{projectEmail}</Bold>.
                Click it to unlock all features.
              </>
            ) : (
              <>
                ส่งลิงก์ยืนยันไปที่ <Bold>{projectEmail}</Bold> แล้ว
                กรุณาคลิกลิงก์เพื่อเปิดใช้งานทุกฟีเจอร์
              </>
            )}
          </ReminderText>
        </Reminder>

        {/* แสดงเมื่อใบเสร็จไปคนละอีเมลเท่านั้น — ถ้าใช้อีเมลเดียวกันการย้ำซ้ำรกเปล่าๆ */}
        {billingEmail && billingEmail !== projectEmail && (
          <Reminder>
            <ReminderTitle>
              <ReminderIcon aria-hidden>🧾</ReminderIcon>
              {lang === "en"
                ? "Receipt / tax invoice"
                : "ใบเสร็จรับเงิน/ใบกำกับภาษี"}
            </ReminderTitle>
            <ReminderText>
              {lang === "en" ? (
                <>
                  Sent to <Bold>{billingEmail}</Bold> — the address you use to
                  sign in to invoize.me.
                </>
              ) : (
                <>
                  ส่งไปที่ <Bold>{billingEmail}</Bold> ซึ่งเป็นอีเมลสำหรับ
                  เข้าใช้งาน invoize.me
                </>
              )}
            </ReminderText>
          </Reminder>
        )}

        <PrimaryButton type="button" onClick={handleGo}>
          {lang === "en" ? "Continue to dashboard →" : "ไปยังระบบ →"}
        </PrimaryButton>
      </Body>
    </StyledModal>
  );
}

export default SuccessModal;

// ─── Animations ──────────────────────────────

const popIn = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
`;

// ─── Styled ──────────────────────────────────

const StyledModal = styled(Modal)`
  && .modal-dialog {
    max-width: 440px;
  }

  && .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    background: #ffffff;
  }
`;

const Body = styled.div`
  padding: 32px 28px;
  text-align: center;
`;

const CheckCircle = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #34d399, #10b981);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
  animation: ${popIn} 0.4s ease-out;
`;

const CheckMark = styled.span`
  color: #ffffff;
  font-size: 38px;
  font-weight: 700;
  line-height: 1;
`;

const Heading = styled.h4`
  && {
    margin: 0 0 8px;
    font-size: 22px;
    font-weight: 700;
    color: #111827;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const Bold = styled.span`
  font-weight: 700;
  color: #111827;
`;

const Reminder = styled.div`
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 24px;
  text-align: left;
`;

const ReminderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #3730a3;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ReminderIcon = styled.span`
  font-size: 14px;
`;

const ReminderText = styled.div`
  font-size: 12px;
  color: #4338ca;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  && {
    width: 100%;
    height: 44px;
    background: #6366f1;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;
    font-family: inherit;

    &:hover {
      background: #4f46e5;
    }
    &:active {
      transform: translateY(1px);
    }
  }
`;